import { useEffect, useState } from "react";
import { ArrowUpload24Regular } from "@fluentui/react-icons";
import { Modal } from "../../components/Modal";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  createConfirmingPayment,
  readConfirmingNotices,
  type ConfirmingBank,
  type ConfirmingNoticeInvoice,
  type ConfirmingNoticeProposal,
  type ConfirmingNoticeRow,
} from "../../confirming/confirmingApi";

const MANUAL = "manual";

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

const addDaysInput = (date: string, days: number): string => {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

const STATUS_LABEL: Record<
  ConfirmingNoticeRow["status"],
  { text: string; className: string }
> = {
  matched: { text: "Pronto", className: "bg-green-500/10 text-green-700" },
  ambiguous: {
    text: "Escolher faturas",
    className: "bg-yellow-500/10 text-yellow-700",
  },
  unmatched: {
    text: "Sem fatura",
    className: "bg-destructive/10 text-destructive",
  },
  existing: { text: "Já no AJA2", className: "bg-muted text-secondary-text" },
};

/** O que o utilizador decide para cada linha do quadro. */
interface RowChoice {
  /** Índice da opção proposta, ou "manual". */
  option: string;
  manualIds: number[];
  paymentDate: string;
  settlementDate: string;
  /** A liquidação veio do quadro do banco (não se recalcula ao mudar de banco). */
  settlementRead: boolean;
  result?: "created" | { error: string };
}

const invoiceLabel = (invoice: ConfirmingNoticeInvoice) =>
  `${invoice.invoiceNumber ?? `#${invoice.workFinancialEntryId}`} · ${formatCurrency(invoice.amount)}`;

const optionLabel = (option: ConfirmingNoticeInvoice[]) =>
  option.length === 1
    ? invoiceLabel(option[0])
    : `${option.map((invoice) => invoice.invoiceNumber ?? `#${invoice.workFinancialEntryId}`).join(" + ")} · ${formatCurrency(
        option.reduce((sum, invoice) => sum + invoice.amount, 0),
      )}`;

function initialChoice(row: ConfirmingNoticeRow, termDays: number): RowChoice {
  const paymentDate = row.paymentDate ? row.paymentDate.slice(0, 10) : "";
  const settlementDate = row.settlementDate
    ? row.settlementDate.slice(0, 10)
    : paymentDate
      ? addDaysInput(paymentDate, termDays)
      : "";
  return {
    // Ambíguo: nada escolhido à partida — quem decide entre as faturas é o utilizador.
    option:
      row.status === "ambiguous" ? "" : row.options.length > 0 ? "0" : MANUAL,
    manualIds: [],
    paymentDate,
    settlementDate,
    settlementRead: row.settlementDate != null,
  };
}

function chosenIds(row: ConfirmingNoticeRow, choice: RowChoice): number[] {
  if (choice.option === MANUAL) return choice.manualIds;
  if (choice.option === "") return [];
  return (row.options[Number(choice.option)] ?? []).map(
    (invoice) => invoice.workFinancialEntryId,
  );
}

/** As faturas escolhidas na linha (proposta ou à mão), com o que falta pagar de cada uma. */
function chosenInvoices(
  row: ConfirmingNoticeRow,
  choice: RowChoice,
): ConfirmingNoticeInvoice[] {
  const ids = chosenIds(row, choice);
  const all = [...row.options.flat(), ...row.supplierInvoices];
  return ids
    .map((id) => all.find((invoice) => invoice.workFinancialEntryId === id))
    .filter((invoice): invoice is ConfirmingNoticeInvoice => invoice != null);
}

/**
 * Porque é que a linha ainda não se pode criar, ou null. O aviso tem de bater com o quadro: várias
 * faturas somam o valor do aviso ao cêntimo (o aviso grava o total de cada uma), uma só não pode
 * ficar com mais do que falta pagar dela, e as datas têm de estar as duas e pela ordem certa.
 */
function blockReason(
  row: ConfirmingNoticeRow,
  choice: RowChoice,
): string | null {
  const invoices = chosenInvoices(row, choice);
  if (invoices.length === 0) return "Escolha as faturas deste aviso.";
  const total = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  if (row.amount != null) {
    if (invoices.length > 1 && Math.abs(total - row.amount) > 0.01) {
      return `As faturas escolhidas somam ${formatCurrency(total)} e o aviso é de ${formatCurrency(row.amount)}.`;
    }
    if (invoices.length === 1 && row.amount > total + 0.01) {
      return `O aviso (${formatCurrency(row.amount)}) é maior do que o que falta pagar da fatura (${formatCurrency(total)}).`;
    }
  }
  if (!choice.paymentDate) return "Indique a data de pagamento ao fornecedor.";
  if (choice.settlementDate && choice.settlementDate < choice.paymentDate) {
    return "A liquidação não pode ser antes do pagamento ao fornecedor.";
  }
  return null;
}

/** A linha ainda se pode criar aqui: não está no AJA2 e há faturas do fornecedor para escolher. */
const isActionable = (row: ConfirmingNoticeRow) =>
  row.status !== "existing" &&
  (row.options.length > 0 || row.supplierInvoices.length > 0);

type RowEntry = { row: ConfirmingNoticeRow; choice: RowChoice; index: number };

/**
 * Importar avisos do banco: o utilizador carrega o print ou o PDF do quadro de APs da app do banco, a
 * IA lê cada linha e o AJA2 propõe as faturas de cada aviso. Só se confirma — os que não bateram
 * ficam assinalados para escolher as faturas à mão ou deixar de fora.
 */
export function ImportNoticesModal({
  banks,
  defaultTermDays,
  onClose,
  onCreated,
}: {
  banks: ConfirmingBank[];
  defaultTermDays: number;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}) {
  const [bankId, setBankId] = useState(
    banks.length === 1 ? String(banks[0].id) : "",
  );
  const [file, setFile] = useState<File | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ConfirmingNoticeProposal | null>(
    null,
  );
  const [choices, setChoices] = useState<RowChoice[]>([]);

  // Um print colado chega sem nome útil ("image.png"): dá-se-lhe um que se perceba no ecrã.
  const acceptPasted = (pasted: Blob & { name?: string }) => {
    const extension =
      pasted.type === "application/pdf"
        ? "pdf"
        : pasted.type === "image/jpeg"
          ? "jpg"
          : "png";
    setFile(
      pasted instanceof File && pasted.name && pasted.name !== "image.png"
        ? pasted
        : new File([pasted], `quadro-colado.${extension}`, {
            type: pasted.type,
          }),
    );
    setError(null);
  };

  // Print tirado para o clipboard: Ctrl+V (Windows) ou Cmd+V (Mac) com o modal aberto carrega-o.
  useEffect(() => {
    if (proposal) return undefined;
    const onPaste = (event: ClipboardEvent) => {
      const pasted = Array.from(event.clipboardData?.files ?? []).find(
        (item) =>
          item.type.startsWith("image/") || item.type === "application/pdf",
      );
      if (!pasted) return;
      event.preventDefault();
      acceptPasted(pasted);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [proposal]);

  // O mesmo pelo botão, para quem não usa o teclado. O browser pede autorização da primeira vez.
  const handlePasteButton = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const type = item.types.find((candidate) =>
          candidate.startsWith("image/"),
        );
        if (type) {
          acceptPasted(await item.getType(type));
          return;
        }
      }
      setError(
        "Não há nenhuma imagem no clipboard. Tire o print do quadro e volte a carregar em Colar print.",
      );
    } catch {
      setError(
        "O browser não deixou ler o clipboard. Use Ctrl+V (ou Cmd+V no Mac) com esta janela aberta.",
      );
    }
  };

  const bank = banks.find((item) => String(item.id) === bankId) ?? null;
  const termDays = bank?.termDays ?? defaultTermDays;

  const updateChoice = (index: number, patch: Partial<RowChoice>) =>
    setChoices((current) =>
      current.map((choice, i) =>
        i === index ? { ...choice, ...patch } : choice,
      ),
    );

  const handleBankChange = (value: string) => {
    setBankId(value);
    const next = banks.find((item) => String(item.id) === value);
    if (!next) return;
    // A liquidação calculada segue o prazo do banco; a lida do quadro fica.
    setChoices((current) =>
      current.map((choice) =>
        choice.settlementRead || !choice.paymentDate
          ? choice
          : {
              ...choice,
              settlementDate: addDaysInput(choice.paymentDate, next.termDays),
            },
      ),
    );
  };

  const handleRead = async () => {
    if (!file || isReading) return;
    if (banks.length > 0 && !bank) {
      setError("Indique de que banco é o quadro.");
      return;
    }
    setIsReading(true);
    setError(null);
    try {
      const read = await readConfirmingNotices(file);
      setProposal(read);
      setChoices(read.rows.map((row) => initialChoice(row, termDays)));
    } catch (err) {
      setError(errorMessage(err, "Não foi possível ler o quadro."));
    } finally {
      setIsReading(false);
    }
  };

  // Uma fatura só entra num aviso: a linha que escolha uma fatura já usada (por um aviso criado aqui ou
  // por uma linha de cima) fica bloqueada até se escolher outra.
  const entries: RowEntry[] = proposal
    ? proposal.rows.map((row, index) => ({
        row,
        choice: choices[index],
        index,
      }))
    : [];
  const claimed = new Set<number>(
    entries
      .filter(({ choice }) => choice.result === "created")
      .flatMap(({ row, choice }) => chosenIds(row, choice)),
  );
  const conflicts = new Set<number>();
  for (const { row, choice, index } of entries) {
    if (!isActionable(row) || choice.result === "created") continue;
    const ids = chosenIds(row, choice);
    if (ids.some((id) => claimed.has(id))) {
      conflicts.add(index);
    } else {
      ids.forEach((id) => claimed.add(id));
    }
  }
  const ready = entries.filter(
    ({ row, choice, index }) =>
      row.status === "matched" &&
      choice.result !== "created" &&
      !conflicts.has(index) &&
      blockReason(row, choice) == null,
  );

  // Um a um, pela ordem do quadro: cada aviso passa pelas validações de sempre (plafond, mesmo
  // fornecedor) e um que falhe não impede os outros.
  const createRows = async (toCreate: RowEntry[]) => {
    if (isSaving || toCreate.length === 0) return;
    setIsSaving(true);
    setError(null);
    setNotice(null);
    let failures = 0;
    const createdNow = new Set<number>();
    for (const { row, choice, index } of toCreate) {
      const invoices = chosenInvoices(row, choice);
      const ids = invoices.map((invoice) => invoice.workFinancialEntryId);
      try {
        await createConfirmingPayment({
          workFinancialEntryId: ids[0],
          additionalWorkFinancialEntryIds: ids.slice(1),
          // Uma fatura: o valor do aviso, ou o que falta pagar dela se o quadro não o trouxe.
          amount: ids.length === 1 ? (row.amount ?? invoices[0].amount) : null,
          issueDate: choice.paymentDate || null,
          dueDate: choice.settlementDate || null,
          reference: row.reference,
          confirmingBankId: bank?.id ?? null,
        });
        updateChoice(index, { result: "created" });
        createdNow.add(index);
      } catch (err) {
        failures += 1;
        updateChoice(index, {
          result: {
            error: errorMessage(err, "Não foi possível criar o aviso."),
          },
        });
      }
    }
    setIsSaving(false);
    await onCreated();

    // A janela só fecha quando não sobra nada para tratar — fechar perdia as linhas por escolher e
    // obrigava a ler o quadro outra vez.
    const pending = entries.filter(
      ({ row, choice, index }) =>
        isActionable(row) &&
        choice.result !== "created" &&
        !createdNow.has(index),
    ).length;

    if (failures > 0) {
      setError(
        `${failures} aviso(s) não foram criados — veja o motivo na linha.`,
      );
    } else if (pending > 0) {
      setNotice(
        `${createdNow.size} aviso(s) criado(s). Faltam ${pending} por tratar — ou feche, se não forem para criar.`,
      );
    } else {
      onClose();
    }
  };

  return (
    <Modal
      title="Importar avisos do banco"
      onClose={onClose}
      maxWidthClass="max-w-3xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 min-h-[44px] border border-border"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {proposal ? "Fechar" : "Cancelar"}
          </button>
          {proposal ? (
            ready.length > 0 && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => createRows(ready)}
                className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {isSaving
                  ? "A criar…"
                  : ready.length === 1
                    ? "Criar o aviso pronto"
                    : `Criar os ${ready.length} avisos prontos`}
              </button>
            )
          ) : (
            <button
              type="button"
              disabled={!file || isReading}
              onClick={handleRead}
              className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {isReading ? "A ler o quadro…" : "Ler quadro"}
            </button>
          )}
        </>
      }
    >
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          className="border border-border bg-green-500/10 text-green-700 px-3 py-2 text-sm"
          style={{ borderRadius: "var(--radius)" }}
        >
          {notice}
        </div>
      )}

      {banks.length > 0 && (
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary-text">Banco</span>
          <select
            aria-label="Banco do quadro"
            value={bankId}
            onChange={(event) => handleBankChange(event.target.value)}
            disabled={isSaving}
            className="px-3 py-2 min-h-[44px] border border-border bg-background text-foreground text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            <option value="">Selecione o banco…</option>
            {banks.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {!proposal ? (
        <div className="space-y-2">
          <label
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const dropped = event.dataTransfer.files?.[0];
              if (dropped) setFile(dropped);
            }}
            className="flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-border text-center text-sm cursor-pointer hover:bg-accent/50"
            style={{ borderRadius: "var(--radius)" }}
          >
            <ArrowUpload24Regular />
            {file ? (
              <span style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                {file.name}
              </span>
            ) : (
              <span style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                Clique para escolher o ficheiro, ou arraste-o para aqui
              </span>
            )}
            <span className="text-secondary-text text-xs">
              Print ou PDF do quadro de avisos na app do banco
            </span>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="hidden"
            />
          </label>
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <button
              type="button"
              onClick={handlePasteButton}
              className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] border border-border text-foreground hover:bg-accent/50 cursor-pointer"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              Colar print
            </button>
            <span className="text-secondary-text text-xs">
              ou Ctrl+V (Cmd+V no Mac) com esta janela aberta
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm">{proposal.summary}</p>
          <p className="text-secondary-text text-xs">
            Os avisos «Pronto» criam-se todos com o botão em baixo. Nos outros,
            escolha as faturas e carregue em «Criar este aviso».
          </p>
          {proposal.rows.map((row, index) => (
            <NoticeRow
              key={index}
              row={row}
              choice={choices[index]}
              termDays={termDays}
              conflict={conflicts.has(index)}
              disabled={isSaving}
              onChange={(patch) => updateChoice(index, patch)}
              onCreate={() =>
                createRows([{ row, choice: choices[index], index }])
              }
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function NoticeRow({
  row,
  choice,
  termDays,
  conflict,
  disabled,
  onChange,
  onCreate,
}: {
  row: ConfirmingNoticeRow;
  choice: RowChoice;
  termDays: number;
  conflict: boolean;
  disabled: boolean;
  onChange: (patch: Partial<RowChoice>) => void;
  onCreate: () => void;
}) {
  const status = STATUS_LABEL[row.status];
  const created = choice.result === "created";
  const failure =
    typeof choice.result === "object" ? choice.result.error : null;
  const ids = chosenIds(row, choice);
  const blocked = blockReason(row, choice);
  const open = isActionable(row) && !created;
  const locked = disabled || !open;

  // Uma fatura paga em parte só pode ser aviso sozinha (o aviso de várias grava o total de cada uma).
  const toggleManual = (invoice: ConfirmingNoticeInvoice) => {
    const id = invoice.workFinancialEntryId;
    const selected = choice.manualIds.includes(id);
    const joinable = row.supplierInvoices.filter(
      (item) =>
        choice.manualIds.includes(item.workFinancialEntryId) && item.canJoin,
    );
    const manualIds = selected
      ? choice.manualIds.filter((item) => item !== id)
      : invoice.canJoin
        ? [...joinable.map((item) => item.workFinancialEntryId), id]
        : [id];
    onChange({ manualIds });
  };

  // A liquidação calculada acompanha a data de pagamento; a lida do quadro (ou escrita à mão) fica.
  const handlePaymentDate = (value: string) =>
    onChange(
      choice.settlementRead
        ? { paymentDate: value }
        : {
            paymentDate: value,
            settlementDate: value ? addDaysInput(value, termDays) : "",
          },
    );

  return (
    <div
      className="border border-border p-3 space-y-2 text-sm"
      style={{ borderRadius: "var(--radius)" }}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <span
          className="inline-flex items-center"
          style={{ fontWeight: "var(--font-weight-semi-bold)" }}
        >
          {row.supplierName ?? "Fornecedor não lido"}
        </span>
        <span className="text-secondary-text inline-flex items-center">
          {row.reference ? `Aviso ${row.reference} · ` : ""}
          {row.amount != null ? formatCurrency(row.amount) : "valor não lido"}
        </span>
        <span
          className={`ml-auto inline-flex items-center px-2 py-1 text-xs ${created ? "bg-green-500/10 text-green-700" : status.className}`}
          style={{ borderRadius: "var(--radius)" }}
        >
          {created ? "Criado" : status.text}
        </span>
      </div>

      <p className="text-secondary-text text-xs">
        {created
          ? "aviso criado no AJA2"
          : row.status !== "existing" && !isActionable(row)
            ? `${row.reason}. Lance a fatura no AJA2 e volte a importar o quadro.`
            : row.reason}
      </p>

      {open && (
        <>
          {row.options.length > 0 && (
            <select
              aria-label="Faturas do aviso"
              value={choice.option}
              disabled={locked}
              onChange={(event) => onChange({ option: event.target.value })}
              className="w-full px-3 py-2 min-h-[44px] border border-border bg-background text-foreground text-sm"
              style={{ borderRadius: "var(--radius)" }}
            >
              {choice.option === "" && (
                <option value="">Escolha as faturas deste aviso…</option>
              )}
              {row.options.map((option, optionIndex) => (
                <option key={optionIndex} value={optionIndex}>
                  {optionLabel(option)}
                </option>
              ))}
              {row.supplierInvoices.length > 0 && (
                <option value={MANUAL}>Escolher as faturas à mão…</option>
              )}
            </select>
          )}

          {choice.option === MANUAL &&
            (row.supplierInvoices.length > 0 ? (
              <div className="space-y-1">
                <span className="text-secondary-text text-xs">
                  Faturas por avisar deste fornecedor
                  {choice.manualIds.length > 0 &&
                    ` · selecionadas ${formatCurrency(
                      row.supplierInvoices
                        .filter((invoice) =>
                          choice.manualIds.includes(
                            invoice.workFinancialEntryId,
                          ),
                        )
                        .reduce((sum, invoice) => sum + invoice.amount, 0),
                    )}`}
                </span>
                {row.supplierInvoices.map((invoice) => (
                  <label
                    key={invoice.workFinancialEntryId}
                    className="flex items-center gap-2 min-h-[44px] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={choice.manualIds.includes(
                        invoice.workFinancialEntryId,
                      )}
                      disabled={locked}
                      onChange={() => toggleManual(invoice)}
                    />
                    <span>
                      {invoiceLabel(invoice)}
                      {invoice.documentDate
                        ? ` · ${formatDateShort(invoice.documentDate)}`
                        : ""}
                      {!invoice.canJoin && " · já paga em parte, só sozinha"}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-xs text-secondary-text">
                Não há faturas por avisar deste fornecedor no AJA2. Lance a
                fatura e volte a importar.
              </p>
            ))}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-secondary-text text-xs">
                Pagamento ao fornecedor
              </span>
              <input
                type="date"
                value={choice.paymentDate}
                disabled={locked}
                onChange={(event) => handlePaymentDate(event.target.value)}
                className="px-3 py-2 min-h-[44px] border border-border bg-background text-foreground text-sm"
                style={{ borderRadius: "var(--radius)" }}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-secondary-text text-xs">
                Liquidação ao banco
                {choice.settlementRead ? " (do quadro)" : " (prazo do banco)"}
              </span>
              <input
                type="date"
                value={choice.settlementDate}
                disabled={locked}
                onChange={(event) =>
                  onChange({
                    settlementDate: event.target.value,
                    settlementRead: true,
                  })
                }
                className="px-3 py-2 min-h-[44px] border border-border bg-background text-foreground text-sm"
                style={{ borderRadius: "var(--radius)" }}
              />
            </label>
          </div>

          {conflict && (
            <p className="text-destructive text-xs">
              Uma destas faturas já está noutro aviso desta lista. Escolha
              outras.
            </p>
          )}
          {!conflict && blocked && ids.length > 0 && (
            <p className="text-destructive text-xs">{blocked}</p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={locked || conflict || blocked != null}
              onClick={onCreate}
              className="px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: "var(--radius-button)" }}
            >
              {ids.length === 0 ? "Escolha as faturas" : "Criar este aviso"}
            </button>
          </div>
        </>
      )}
      {failure && <p className="text-destructive text-xs">{failure}</p>}
    </div>
  );
}
